import styled, { css, keyframes } from 'styled-components';
import {
  PROFILE_MENU_ITEM_VARIANT_DANGER,
  type ProfileMenuItemVariant,
} from './ProfileMenu.constants';

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const Panel = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: min(360px, 92vw);
  max-height: min(560px, 80vh);
  display: flex;
  flex-direction: column;
  z-index: 100;
  overflow: hidden;
  animation: ${slideIn} 160ms cubic-bezier(0.4, 0, 0.2, 1);

  ${({ theme: { colors = {}, borderRadius = {} } }) => css`
    background-color: ${colors.surfaceContainerLow ?? '#1a1a1a'};
    color: ${colors.onSurface ?? '#fff'};
    border: 1px solid ${colors.outlineVariant ?? '#555'};
    border-radius: ${borderRadius.large ?? '16px'};
    box-shadow:
      0 24px 56px rgba(0, 0, 0, 0.5),
      0 2px 8px rgba(0, 0, 0, 0.3);
  `}
`;

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px 12px;
`;

export const HeaderName = styled.div`
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.01em;

  ${({ theme: { colors = {} } }) => css`
    color: ${colors.onSurface ?? '#fff'};
  `}
`;

export const HeaderMeta = styled.div`
  font-size: 12px;

  ${({ theme: { colors = {} } }) => css`
    color: ${colors.onSurfaceVariant ?? '#aaa'};
  `}
`;

export const Divider = styled.div`
  height: 1px;
  margin: 2px 0;

  ${({ theme: { colors = {} } }) => css`
    background-color: ${colors.outlineVariant ?? '#555'};
  `}
`;

export const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 0 1 auto;
`;

export const SectionLabel = styled.div`
  padding: 12px 16px 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  ${({ theme: { colors = {} } }) => css`
    color: ${colors.onSurfaceVariant ?? '#aaa'};
  `}
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1 1 auto;
  overflow: hidden;
`;

export const Footer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px 8px 8px;

  ${({ theme: { spacing = {} } }) => css`
    gap: ${spacing.level1 ?? '2px'};
  `}
`;

export const MenuItem = styled.button<{ $variant: ProfileMenuItemVariant }>`
  display: flex;
  align-items: center;
  width: 100%;
  margin: 2px 6px;
  padding: 10px 14px;
  background: transparent;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  font-size: 13.5px;
  font-weight: 500;
  text-align: left;
  font-family: inherit;
  transition: background-color 120ms ease;

  ${({ theme: { spacing = {} } }) => css`
    gap: ${spacing.level3 ?? '8px'};
  `}

  ${({ $variant, theme: { colors = {} } }) =>
    $variant === PROFILE_MENU_ITEM_VARIANT_DANGER
      ? css`
          color: ${colors.error ?? '#f55'};
          &:hover {
            background-color: rgb(255 180 171 / 0.08);
          }
        `
      : css`
          color: ${colors.onSurface ?? '#fff'};
          &:hover {
            background-color: ${colors.surfaceContainerHigh ?? colors.surfaceContainer ?? '#333'};
          }
        `}

  &:focus-visible {
    outline: 2px solid ${({ theme: { colors = {} } }) => colors.primary ?? '#88f'};
    outline-offset: 2px;
  }

  /* Pin width to the panel minus the 6px horizontal margins so hover state is uniform. */
  width: calc(100% - 12px);
`;

export const MenuItemIcon = styled.span<{ $variant: ProfileMenuItemVariant }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;

  ${({ $variant, theme: { colors = {} } }) =>
    $variant === PROFILE_MENU_ITEM_VARIANT_DANGER
      ? css`
          color: ${colors.error ?? '#f55'};
        `
      : css`
          color: ${colors.onSurfaceVariant ?? '#aaa'};
        `}

  & > svg {
    width: 18px;
    height: 18px;
  }
`;

export const MenuItemLabel = styled.span`
  flex: 1;
  min-width: 0;
`;
